import { Component, inject, signal, OnDestroy, computed, Signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { dateFormattingOptions } from '@domain/formatting';
import { HaulierItem } from '@hjc/masterdata-types';
import { PartnersService } from '@services/partners.service';
import { ColDef, RowClickedEvent, GridApi } from 'ag-grid-community';
import { AgGridAngular } from 'ag-grid-angular';
import Fuse from 'fuse.js';
import { debounceTime, Subscription } from 'rxjs';
import { PageHeaderComponent } from '@components/page-header/page-header.component';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { BaseDataGridComponent } from '@components/base-data-grid.component';

@Component({
  selector: 'md-view-logistics-page',
  imports: [PageHeaderComponent, MatFormFieldModule, MatInputModule, MatIconModule, MatButtonModule, AgGridAngular, ReactiveFormsModule, TranslateModule],
  templateUrl: './view-logistics-page.component.html',
  styleUrl: './view-logistics-page.component.scss',
  standalone: true
})
export class ViewLogisticsPageComponent extends BaseDataGridComponent implements OnDestroy {
  private partnersService = inject(PartnersService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  private gridApi = signal<GridApi<HaulierItem> | null>(null);

  isLoading = signal(false);
  isExporting = signal(false);
  allPartners = signal<HaulierItem[]>([]);
  displayPartners = signal<HaulierItem[]>([]);

  searchInput = new FormControl<string>('', { nonNullable: true });
  searchTermSubscription?: Subscription;
  fuse?: Fuse<HaulierItem>;

  columnDefs: Signal<ColDef<HaulierItem>[]> = computed(() => {
    const getTranslatedHeader = (key: string) => (this.translationsReady() ? this.translate.instant(key) : key);

    return [
      { headerName: getTranslatedHeader('FIELDS.IDENTIFIER'), field: 'id', sortable: true, flex: 1 },
      { headerName: getTranslatedHeader('INFO.NAME'), field: `name.${this.lang()}`, sortable: true, flex: 1 },
      { headerName: getTranslatedHeader('FIELDS.STREET_ADDRESS'), field: 'address', sortable: true, flex: 1 },
      { headerName: getTranslatedHeader('FIELDS.POSTAL_CODE'), field: 'zip', sortable: true, flex: 1 },
      { headerName: getTranslatedHeader('FIELDS.CITY'), field: 'city', sortable: true, flex: 1 },
      { headerName: getTranslatedHeader('FIELDS.LICENSE_NUMBER'), field: 'licenseId', sortable: true, flex: 1 },
      { headerName: getTranslatedHeader('FIELDS.SYSTEM.CREATED_BY'), valueGetter: p => p.data?.createdBy, sortable: true, filter: true, flex: 1 },
      {
        headerName: getTranslatedHeader('FIELDS.SYSTEM.CREATED_AT'),
        field: 'createdAt',
        valueFormatter: p => new Date(p.value).toLocaleString(this.lang(), dateFormattingOptions),
        sortable: true,
        flex: 1
      },
      { headerName: getTranslatedHeader('FIELDS.SYSTEM.UPDATED_BY'), valueGetter: p => p.data?.updatedBy, sortable: true, filter: true, flex: 1 },
      {
        headerName: getTranslatedHeader('FIELDS.SYSTEM.UPDATED_AT'),
        field: 'updatedAt',
        valueFormatter: p => new Date(p.value).toLocaleString(this.lang(), dateFormattingOptions),
        sortable: true,
        flex: 1
      }
    ];
  });

  constructor() {
    super();
    this.translate.onLangChange.subscribe(() => this.initFuse());
    this.init();
  }

  onRowClicked(event: RowClickedEvent<HaulierItem>) {
    const rowItemId = event.data?.id;
    if (rowItemId) {
      this.router.navigate([rowItemId], { relativeTo: this.route });
    }
  }

  async init() {
    this.isLoading.set(true);
    const partners = (await this.partnersService.getLogisticsPartners())?.data;
    this.allPartners.set(partners);
    this.displayPartners.set(partners);
    this.initFuse();
    this.searchTermSubscription = this.searchInput.valueChanges.pipe(debounceTime(300)).subscribe(term => this.search(term));
    this.isLoading.set(false);
  }

  initFuse() {
    this.fuse = new Fuse(this.allPartners(), {
      keys: ['id', `name.${this.lang()}`, 'address', 'postalCode', 'city', 'licenseId'],
      includeScore: true,
      threshold: 0.3
    });
  }

  search(term: string) {
    if (!this.fuse) return;
    this.displayPartners.set(term && term.length > 0 ? this.fuse.search(term).map(r => r.item) : this.allPartners());
  }

  onGridReady(params: { api: GridApi<HaulierItem> }) {
    this.gridApi.set(params.api);
  }

  async exportToExcel() {
    const api = this.gridApi();
    if (!api) return;

    await this.exportGridToExcel(api, this.columnDefs(), 'logistics-partners', this.isExporting);
  }

  ngOnDestroy() {
    this.searchTermSubscription?.unsubscribe();
  }
}
